import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays, X, ShoppingCart, Gift } from "lucide-react";
import { toast } from "sonner";
import api, { formatPrice } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Loader } from "@/components/Loader";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

export default function Planner() {
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [meals, setMeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState({}); // day -> meal_id
  const [picking, setPicking] = useState(null); // day currently choosing

  useEffect(() => {
    api
      .get("/meals?category=All")
      .then((r) => setMeals((r.data.meals || []).filter((m) => !m.is_custom_box)))
      .catch(() => toast.error("Could not load meals."))
      .finally(() => setLoading(false));
  }, []);

  const mealById = (id) => meals.find((m) => m.meal_id === id);

  const chosen = DAYS.map((d) => plan[d]).filter(Boolean);
  const weekSubtotal = chosen.reduce((s, id) => s + (mealById(id)?.price_cents || 0), 0);
  const cheapest = chosen.length >= 5 ? Math.min(...chosen.map((id) => mealById(id)?.price_cents || 0)) : 0;

  const addWeek = () => {
    if (chosen.length === 0) {
      toast.error("Pick at least one lunch first.");
      return;
    }
    DAYS.forEach((d) => {
      const m = mealById(plan[d]);
      if (m) addItem(m, 1);
    });
    toast.success(`${chosen.length} lunches added to cart! 🗓️`);
    navigate("/cart");
  };

  if (loading) return <Loader label="Building the calendar…" />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display font-bold text-3xl sm:text-4xl text-slate-800 tracking-tight flex items-center gap-3">
          <CalendarDays className="text-sky-500" /> Weekly Planner
        </h1>
        <p className="text-slate-500 mt-1">
          Pick a lunch for each day — order 5 and the cheapest one is <span className="font-semibold text-emerald-600">free!</span>
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {DAYS.map((day) => {
          const m = mealById(plan[day]);
          return (
            <div
              key={day}
              data-testid={`planner-day-${day.toLowerCase()}`}
              className="bg-white rounded-3xl border-2 border-emerald-100/80 p-4 flex flex-col"
            >
              <p className="font-display font-bold text-slate-800 mb-3">{day}</p>
              {m ? (
                <div className="relative">
                  <img src={m.image_url} alt={m.name} className="w-full h-28 rounded-2xl object-cover" />
                  <button
                    data-testid={`planner-clear-${day.toLowerCase()}`}
                    onClick={() => setPlan((p) => ({ ...p, [day]: undefined }))}
                    className="absolute -top-2 -right-2 h-7 w-7 rounded-full bg-rose-400 text-white flex items-center justify-center shadow"
                  >
                    <X size={15} />
                  </button>
                  <p className="font-semibold text-sm text-slate-800 mt-2 line-clamp-1">{m.name}</p>
                  <p className="text-emerald-600 font-bold text-sm">{formatPrice(m.price_cents)}</p>
                </div>
              ) : (
                <button
                  data-testid={`planner-pick-${day.toLowerCase()}`}
                  onClick={() => setPicking(day)}
                  className="flex-1 min-h-[130px] rounded-2xl border-2 border-dashed border-emerald-200 text-emerald-500 font-semibold hover:bg-emerald-50 transition-colors flex items-center justify-center"
                >
                  + Pick lunch
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Sticky summary */}
      <div className="bg-white rounded-3xl border-2 border-emerald-100 p-5 flex flex-wrap items-center justify-between gap-4 sticky bottom-4 shadow-lg">
        <div>
          <p className="text-slate-500 font-medium">{chosen.length} of 5 days planned</p>
          <p className="font-display font-bold text-2xl text-slate-800">
            {formatPrice(weekSubtotal)}
            {cheapest > 0 && (
              <span className="text-emerald-600 text-base font-semibold ml-2 inline-flex items-center gap-1">
                <Gift size={16} /> save {formatPrice(cheapest)}
              </span>
            )}
          </p>
        </div>
        <button
          data-testid="add-week-to-cart"
          onClick={addWeek}
          className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-display font-bold text-lg rounded-2xl px-6 py-3 shadow-md shadow-emerald-200 transition-all active:scale-[0.98]"
        >
          <ShoppingCart size={20} /> Add Week to Cart
        </button>
      </div>

      {/* Pick modal */}
      {picking && (
        <div
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={() => setPicking(null)}
        >
          <div
            className="bg-white rounded-3xl w-full max-w-2xl max-h-[80vh] overflow-y-auto p-5"
            onClick={(e) => e.stopPropagation()}
            data-testid="planner-pick-modal"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-xl text-slate-800">Pick lunch for {picking}</h3>
              <button onClick={() => setPicking(null)} className="text-slate-400 hover:text-slate-600">
                <X size={22} />
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {meals.map((m) => (
                <button
                  key={m.meal_id}
                  data-testid={`planner-option-${m.meal_id}`}
                  onClick={() => {
                    setPlan((p) => ({ ...p, [picking]: m.meal_id }));
                    setPicking(null);
                  }}
                  className="text-left rounded-2xl border-2 border-emerald-100 hover:border-emerald-400 overflow-hidden transition-all"
                >
                  <img src={m.image_url} alt={m.name} className="w-full h-24 object-cover" />
                  <div className="p-2">
                    <p className="font-semibold text-sm text-slate-800 line-clamp-1">{m.name}</p>
                    <p className="text-emerald-600 font-bold text-sm">{formatPrice(m.price_cents)}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
